// Client-side password rules for the accept-invite form. They must stay a superset of wms-api's
// `acceptInvite` validation (min 8, uppercase, lowercase, digit, special) so a password the form
// accepts is never rejected by POST /v1/invites/:token/accept. Each pattern is checked in order and
// the first one that fails is the message shown.
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_LENGTH_MESSAGE = `Password must have at least ${PASSWORD_MIN_LENGTH} characters`;
export const PASSWORD_PATTERNS = [
  [/[@$!%*#?&]/, 'Password should contain at least one special character'],
  [/[\d]/, 'Password should contain at least one number'],
  [/[A-Z]/, 'Password should contain at least one uppercase letter'],
  [/[a-z]/, 'Password should contain at least one lowercase letter'],
];

// Interprets the wms-api `GET /invites/:token/check` response for the accept-invite screen.
// wms-api sends `userExists` (camelCase); `user_exists` is kept as a fallback for older responses.
// EN-4052: check never adds an existing account. It joins through accept, signed in as the invited email, so the
// screen offers "Join" only to that signed-in account and otherwise asks the invitee to sign in first.
// `signedInEmail` is the email of the WMS user signed in on this browser, or empty.
export const describeInviteCheck = (result, signedInEmail = '') => {
  if (!result) {
    return {
      kind: 'invalid',
      message: 'Invalid link, please contact your administrator',
    };
  }

  const orgName = result.organizationId?.name || '';

  if (result.userExists || result.user_exists) {
    const inviteEmail = result.email || '';
    const org = orgName || 'the organization';
    if (
      signedInEmail &&
      inviteEmail &&
      signedInEmail.toLowerCase() === inviteEmail.toLowerCase()
    ) {
      return {
        kind: 'join',
        orgName,
        message: `Join ${org} as ${inviteEmail}.`,
      };
    }
    if (signedInEmail) {
      return {
        kind: 'existing',
        orgName,
        message: `This invitation is for ${inviteEmail}, and you are signed in as ${signedInEmail}. Sign out, sign in as ${inviteEmail}, then open this invitation link again.`,
      };
    }
    return {
      kind: 'existing',
      orgName,
      message:
        result.data ||
        `You already have an account. Sign in as ${inviteEmail}, then open this invitation link again to join ${org}.`,
    };
  }

  return {
    kind: 'new',
    orgName,
    firstName: result.firstName,
    lastName: result.lastName,
  };
};

// Message for a failed `POST /invites/:token/accept` by a signed-in invitee (EN-4052 codes from wms-api).
export const describeJoinError = (error, inviteEmail = '') => {
  const who = inviteEmail || 'the invited email';
  if (error?.code === 'sign_in_required')
    return `Sign in as ${who}, then open this invitation link again.`;
  if (error?.code === 'invite_email_mismatch')
    return `This invitation is for ${who}. Sign in with that account to accept it.`;
  return 'Could not join the organization. Please try again or contact your administrator.';
};
